import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

// ════════════════════════════════════════════════════════════════════════
// Button — shadcn/ui Button shape, branded for tktic.
// Variants: primary (cyan), secondary (navy), outline, ghost, link, flag.
// Sizes: sm, default, lg, icon.
// ════════════════════════════════════════════════════════════════════════

export const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-display font-semibold leading-none",
    "transition-colors duration-fast ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        // Primary — cyan CTA. The hero action ("Mark this moment").
        primary: "bg-accent text-accent-ink hover:bg-accent-hover",
        // Secondary — solid navy. Less hot than primary, more visible than ghost.
        secondary: "bg-ink text-ink-inverse hover:bg-navy-700",
        // Outline — for cards, dialogs.
        outline:
          "border border-rule-strong bg-transparent text-ink hover:bg-field",
        // Ghost — minimal, for toolbars.
        ghost: "bg-transparent text-ink hover:bg-field",
        // Link — inline.
        link: "bg-transparent text-accent hover:underline underline-offset-4 p-0 h-auto",
        // Flag — for review moments (orange, not red).
        flag: "bg-flag text-navy hover:opacity-90",
      },
      size: {
        sm: "h-8 px-3 text-sm rounded",
        default: "h-10 px-4 text-base rounded-md",
        lg: "h-12 px-6 text-lg rounded-md",
        icon: "h-10 w-10 rounded-md",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
